import { CoherenceHeader } from '@coherence-design-system/controls/lib/header/CoherenceHeader';
import {
  FarRightFeedbackPanelProps,
  FarRightNotificationPanelProps,
  FarRightProfilePanelProps,
  FarRightSettingsPanelProps,
  ICoherenceHeaderProps,
  SearchSettings,
} from '@coherence-design-system/controls/lib/header/CoherenceHeader.types';
import { ICommandBarItemProps, IPanelHeaderRenderer } from '@fluentui/react';
import { Context } from '@micro-frontend-react/employee-experience/lib/Context';
import { IEmployeeExperienceContext } from '@micro-frontend-react/employee-experience/lib/IEmployeeExperienceContext';
import { EventType, UsageEventName, UserEvent } from '@micro-frontend-react/employee-experience/lib/UsageTelemetry';
import { useGraphPhoto } from '@micro-frontend-react/employee-experience/lib/useGraphPhoto';
import { useUser } from '@micro-frontend-react/employee-experience/lib/useUser';
import * as React from 'react';
import { HeaderRoot, HelpConfigStyles } from './Header.styled';
import { HeaderPanel } from './Header.types';
import { NotificationBell } from '../PBT/NotificationBell';

const NotificationCommandBarButton: React.FC<ICommandBarItemProps> = () => <NotificationBell />;

export function Header({
  farRightSettings,
  searchSettings,
  showNotifications = true,
  ...otherProps
}: ICoherenceHeaderProps & { showNotifications?: boolean }): React.ReactElement {
  const { telemetryClient, authClient } = React.useContext(Context as React.Context<IEmployeeExperienceContext>);
  const user = useUser();
  const photo = useGraphPhoto();
  const searchConfig: SearchSettings | undefined = searchSettings;
  if (searchConfig) {
    const originalOnSearch = searchConfig.onSearch;
    searchConfig.onSearch = (searchTerm: string): void => {
      if (!originalOnSearch) return;

      const searchEvent: UserEvent = {
        subFeature: 'Header.Search',
        businessTransactionId: searchTerm,
        type: EventType.User,
        eventName: UsageEventName.TextChanged,
      };
      telemetryClient.trackEvent(searchEvent);

      originalOnSearch(searchTerm);
    };
  }

  const handleLogOutClicked = (): void => {
    const logout: UserEvent = {
      subFeature: 'Header.Logout',
      type: EventType.User,
      eventName: UsageEventName.ButtonClicked,
    };
    telemetryClient.trackEvent(logout);

    authClient.logOut().catch();
  };

  const getPanelOpenHandler = (panel: HeaderPanel): (() => void) => {
    return (): void => {
      const panelEvent: UserEvent = {
        subFeature: `Header.${panel.toString()}`,
        type: EventType.User,
        eventName: UsageEventName.PanelOpened,
        businessTransactionId: panel.toString(),
      };
      telemetryClient.trackEvent(panelEvent);
      telemetryClient.startTrackPage(panel);
    };
  };

  const getPanelDismissHandler = (panel: HeaderPanel): (() => void) => {
    return (): void => {
      const panelEvent: UserEvent = {
        subFeature: `Header.${panel.toString()}`,
        type: EventType.User,
        eventName: UsageEventName.PanelClosed,
        businessTransactionId: panel.toString(),
      };
      telemetryClient.trackEvent(panelEvent);
      telemetryClient.stopTrackPage(panel);
    };
  };

  const notificationConfig: FarRightNotificationPanelProps | undefined = farRightSettings?.notificationsSettings;
  if (notificationConfig?.panelSettings) {
    notificationConfig.panelSettings.onOpened = getPanelOpenHandler(HeaderPanel.NotificationPanel);
    notificationConfig.panelSettings.onDismissed = getPanelDismissHandler(HeaderPanel.NotificationPanel);
  }

  const settingsConfig: FarRightSettingsPanelProps | undefined = farRightSettings?.settingsSettings;
  if (settingsConfig) {
    settingsConfig.panelSettings.onOpened = getPanelOpenHandler(HeaderPanel.SettingsPanel);
    settingsConfig.panelSettings.onDismissed = getPanelDismissHandler(HeaderPanel.SettingsPanel);
  }

  const panelHeaderRenderer: IPanelHeaderRenderer = () => {
    return <h2></h2>;
  };

  const helpConfig: FarRightSettingsPanelProps | undefined = farRightSettings?.helpSettings;

  if (helpConfig?.panelSettings) {
    helpConfig.panelSettings.onOpened = getPanelOpenHandler(HeaderPanel.HelpPanel);
    helpConfig.panelSettings.onDismissed = getPanelDismissHandler(HeaderPanel.HelpPanel);
    helpConfig.panelSettings.style = HelpConfigStyles;
    helpConfig.panelSettings.isBlocking = false;
    helpConfig.panelSettings.onRenderHeader = panelHeaderRenderer;
    helpConfig.panelSettings.hasCloseButton = true;
  }

  const feedbackConfig: FarRightFeedbackPanelProps | undefined = farRightSettings?.feedbackSettings;
  if (feedbackConfig) {
    const prevOnClick = farRightSettings?.feedbackSettings?.panelSettings.onClick;
    feedbackConfig.panelSettings.onClick = () => {
      if (!prevOnClick) return false;

      getPanelOpenHandler(HeaderPanel.FeedbackPanel);
      return prevOnClick();
    };
  }

  const profileConfig: FarRightProfilePanelProps = {
    ...farRightSettings?.profileSettings,
    panelSettings: {
      ...farRightSettings?.profileSettings?.panelSettings,
      logOutLink: 'javascript:void(0);',
      fullName: user?.name ?? '',
      emailAddress: user?.email ?? '',
      imageUrl: photo || undefined,
      onLogOut: handleLogOutClicked,
      onOpened: getPanelOpenHandler(HeaderPanel.ProfilePanel),
      onDismissed: getPanelDismissHandler(HeaderPanel.ProfilePanel),
      closeButtonAriaLabel: 'Close',
    },
  };

  const notificationItem: ICommandBarItemProps = {
    key: 'pbt-notifications',
    commandBarButtonAs: NotificationCommandBarButton,
  };
  const additionalItems = showNotifications
    ? [notificationItem, ...(farRightSettings?.additionalItems ?? [])]
    : farRightSettings?.additionalItems;
  const handleHeaderKeyDownCapture = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.defaultPrevented || event.key !== 'Tab' || event.shiftKey) return;

    const banner = (event.target as HTMLElement).closest('[role="banner"]');
    if (!banner || event.target !== banner.querySelector('a')) return;

    const nextHeaderAction = banner.querySelector<HTMLElement>(
      'button[aria-label^="Notifications"], #coher-header-overflow-button-id'
    );
    if (nextHeaderAction) {
      event.preventDefault();
      event.stopPropagation();
      nextHeaderAction.focus();
    }
  };

  return (
    <HeaderRoot onKeyDownCapture={handleHeaderKeyDownCapture}>
      <CoherenceHeader
        {...otherProps}
        farRightSettings={{
          ...farRightSettings,
          helpSettings: helpConfig,
          feedbackSettings: feedbackConfig,
          profileSettings: profileConfig,
          additionalItems,
        }}
      />
    </HeaderRoot>
  );
}
